import axiosClient from './axios'

export interface ImmunoblotBand {
  id: string
  name: string
  molecularWeight: number
  zone: string
  required: boolean
  description: string
}

export interface ImmunoblotConfig {
  id: string
  name: string
  fullName: string
  manufacturer: string
  version?: string
  pathology: string
  antibodyClass?: string
  description?: string
  bands: {
    specific: ImmunoblotBand[]
    nonSpecific?: ImmunoblotBand[]
    reference: {
      minKDa: number
      maxKDa: number
      primaryZone: string
    }
  }
  clinicalNotes?: {
    indication?: string
    performance?: {
      sensitivity: string
      sensitivityCI?: string
      specificity: string
      specificityCI?: string
    }
    limitations?: string[]
  }
  interpretation: {
    positive: {
      criteria: Array<{
        type: string
        value?: number
        bandId?: string
        description: string
      }>
      operator: string
      result: string
      conclusion: string
    }
    negative: {
      criteria: Array<{
        type: string
        description: string
      }>
      result: string
      conclusion: string
    }
    equivocal: {
      criteria: Array<{
        type: string
        description: string
      }>
      result: string
      conclusion: string
    }
  }
  qualityControl?: {
    positiveControl: {
      description: string
      minBands: number
      requiredBands: string[]
    }
    negativeControl: {
      description: string
      maxBands: number
    }
  }
  displaySettings?: {
    showMolecularWeights: boolean
    showBandLabels: boolean
    highlightRequiredBands: boolean
    colorScheme: {
      requiredBand: string
      specificBand: string
      detectedBand: string
    }
  }
  metadata?: {
    noticeUrl: string
    lastUpdated: string
    extractedBy: string
    validated: boolean
  }
  isActive: boolean
  createdAt: string
  updatedAt: string
}

// Get all immunoblot configurations
export const getImmunoblotConfigs = (params?: {
  pathology?: string
  activeOnly?: boolean
}) => {
  const queryParams = new URLSearchParams()
  if (params?.pathology) queryParams.append('pathology', params.pathology)
  if (params?.activeOnly !== undefined)
    queryParams.append('activeOnly', params.activeOnly.toString())

  return axiosClient.get<{
    success: boolean
    count: number
    data: ImmunoblotConfig[]
  }>(`immunoblot-configs?${queryParams.toString()}`)
}

// Get specific immunoblot configuration by ID
export const getImmunoblotConfig = (id: string) =>
  axiosClient.get<{
    success: boolean
    data: ImmunoblotConfig
  }>(`immunoblot-configs/${id}`)

// Get immunoblot configurations for a specific pathology
export const getImmunoblotConfigsByPathology = (pathology: string) =>
  axiosClient.get<{
    success: boolean
    pathology: string
    count: number
    data: ImmunoblotConfig[]
  }>(`immunoblot-configs/pathology/${pathology}`)
